import classnames from 'classnames';
import React, { ElementRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps, useHistory, useLocation, useParams } from 'react-router-dom';
import ZoomMediaContext from '../../context/media-context';
import ZoomContext from '../../context/zoom-context';
import ChatContainer from '../chat/chat';
import Avatar from './components/avatar';
import Pagination from './components/pagination';
import RemoteCameraControlPanel from './components/remote-camera-control';
import ShareView from './components/share-view';
import VideoFooter from './components/video-footer';
import AvatarActionContext from './context/avatar-context';
import { useAvatarAction } from './hooks/useAvatarAction';
import { useActiveVideo } from './hooks/useAvtiveVideo';
import { useCanvasDimension } from './hooks/useCanvasDimension';
import { useGalleryLayout } from './hooks/useGalleryLayout';
import { useNetworkQuality } from './hooks/useNetworkQuality';
import { usePagination } from './hooks/usePagination';
import { SELF_VIDEO_ID } from './video-constants';

import { Flex } from '@chakra-ui/react';
import useLessonContext from '../../hooks/useLessonContext';
import { isMobile } from '../../utils/DeviceType';
import useLanguageSelector from '../../hooks/useLanguageSelector';
import { enterPictureInPicture } from '../../utils/pip-utils';
import ChatProvider from './context/chat-context';
import { useChatView } from './hooks/useChatView';
import { usePiPWindow } from './hooks/usePiP';
import './video.scss';
import { IS_DEMO } from '../../config/env-config';
import { JoinService } from '../../service/join-service';
import useUserContext from '../../hooks/useUserContext';
import { UrlPramsPreview } from '../../App';
import useLesson from '../../hooks/useLesson';
import { useUser } from '../../hooks/useUser';
import { SendLoggerService } from '../../service/send-logger-service';
const VideoContainer: React.FunctionComponent<RouteComponentProps> = (props) => {
  const LANG = useLanguageSelector();
  const zmClient = useContext(ZoomContext);
  const {
    mediaStream,
    video: { decode: isVideoDecodeReady }
  } = useContext(ZoomMediaContext);
  const videoRef = useRef<HTMLCanvasElement | null>(null);
  const shareViewRef = useRef<{ selfShareRef: HTMLCanvasElement | HTMLVideoElement | null }>(null);
  const [isRecieveSharing, setIsRecieveSharing] = useState(false);
  const canvasDimension = useCanvasDimension(mediaStream, videoRef);
  const activeVideo = useActiveVideo(zmClient);
  const { page, pageSize, totalPage, totalSize, setPage } = usePagination(zmClient, canvasDimension);
  const { visibleParticipants, layout: videoLayout } = useGalleryLayout(
    zmClient,
    mediaStream,
    isVideoDecodeReady,
    videoRef,
    canvasDimension,
    {
      page,
      pageSize,
      totalPage,
      totalSize
    }
  );
  /**
   * position for self video
   */
  const currentUserIndex = visibleParticipants.findIndex(
    (user) => user.userId === zmClient.getCurrentUserInfo()?.userId
  );
  const currentPage = useLocation().pathname;
  const sendLoggerService = useMemo(() => new SendLoggerService(), []);

  let selfVideoLayout = null;
  if (currentUserIndex > -1) {
    const item = videoLayout[currentUserIndex];
    if (item && canvasDimension) {
      selfVideoLayout = { ...item, y: canvasDimension.height - item.y - item.height };
    }
  }
  const avatarActionState = useAvatarAction(zmClient, visibleParticipants);
  const networkQuality = useNetworkQuality(zmClient);
  const { isOpenChat, togleChatView } = useChatView();
  const history = useHistory();
  const lessonCtx = useLessonContext();
  const userCtx = useUserContext();
  const joinService = useMemo(() => new JoinService(), []);
  const roomId = useParams<UrlPramsPreview>().roomId;
  const companyId = useParams<UrlPramsPreview>().companyId;
  const { lessonInfo } = useLesson(companyId, roomId);
  const { userInfo } = useUser();

  useEffect(() => {
    // セッション開始前にここに来た場合
    const session = zmClient.getSessionInfo();
    if (session.isInMeeting) {
      return;
    }

    if (!userInfo) {
      return;
    }
    if (!lessonInfo) {
      return;
    }
    joinService.joinRoom(userInfo, lessonInfo, zmClient);
  }, [userInfo, lessonInfo, zmClient]);

  const videoContainerRef = useRef<ElementRef<'div'>>(null);

  const startPiPView = useCallback(() => {
    videoContainerRef.current && enterPictureInPicture(videoContainerRef.current, LANG.KT_0100_33);
  }, [videoContainerRef.current, enterPictureInPicture]);
  const { isSupported, requestPipWindow, pipWindow, closePipWindow } = usePiPWindow();
  // const startPiPView = useCallback(() => {
  //   requestPipWindow(200, 500);
  // }, [requestPipWindow]);

  const handleChatButton = () => {
    sendLoggerService.sendLog({
      action: isOpenChat ? 'チャットボタン押下(Open)' : 'チャットボタン押下(Close)',
      lessonId: lessonCtx?.lessonId ?? '',
      roomId: lessonCtx?.roomId ?? '',
      userId: `${zmClient.getSessionInfo().userId}`,
      page: currentPage,
      advancedInfo: {}
    });

    togleChatView();
  };

  return (
    <Flex flexDirection={isMobile() ? 'column' : 'row'}>
      <div className={classnames('viewport')}>
        <ShareView ref={shareViewRef} onRecieveSharingChange={setIsRecieveSharing} />
        {/* {isSupported && pipWindow ? (
          <PiPWindow pipWindow={pipWindow}>
            <div
              className={classnames('video-container', {
                'video-container-in-sharing': isRecieveSharing
              })}
              ref={videoContainerRef}
            >
              <canvas className="video-canvas" id="video-canvas" width="800" height="600" ref={videoRef} />
              {selfVideoLayout && mediaStream?.isRenderSelfViewWithVideoElement() && (
                <video
                  id={SELF_VIDEO_ID}
                  className="self-video-tag"
                  playsInline
                  muted
                  autoPlay
                  style={{
                    display: 'block',
                    width: `${selfVideoLayout.width}px`,
                    height: `${selfVideoLayout.height}px`,
                    top: `${selfVideoLayout.y}px`,
                    left: `${selfVideoLayout.x}px`,
                    pointerEvents: 'none'
                  }}
                />
              )}
              <AvatarActionContext.Provider value={avatarActionState}>
                <ul className="avatar-list">
                  {visibleParticipants.map((user, index) => {
                    if (index > videoLayout.length - 1) {
                      return null;
                    }
                    const dimension = videoLayout[index];
                    const { width, height, x, y } = dimension;
                    const { height: canvasHeight } = canvasDimension;
                    return (
                      <Avatar
                        participant={user}
                        key={user.userId}
                        isActive={activeVideo === user.userId}
                        networkQuality={networkQuality[`${user.userId}`]}
                        style={{
                          width: `${width}px`,
                          height: `${height}px`,
                          top: `${canvasHeight - y - height}px`,
                          left: `${x}px`
                        }}
                      />
                    );
                  })}
                </ul>
                <RemoteCameraControlPanel />
              </AvatarActionContext.Provider>
            </div>
          </PiPWindow>
        ) : (
          <div
            className={classnames('video-container', {
              'video-container-in-sharing': isRecieveSharing
            })}
            ref={videoContainerRef}
          >
            <canvas className="video-canvas" id="video-canvas" width="800" height="600" ref={videoRef} />
            {selfVideoLayout && mediaStream?.isRenderSelfViewWithVideoElement() && (
              <video
                id={SELF_VIDEO_ID}
                className="self-video-tag"
                playsInline
                muted
                autoPlay
                style={{
                  display: 'block',
                  width: `${selfVideoLayout.width}px`,
                  height: `${selfVideoLayout.height}px`,
                  top: `${selfVideoLayout.y}px`,
                  left: `${selfVideoLayout.x}px`,
                  pointerEvents: 'none'
                }}
              />
            )}
            <AvatarActionContext.Provider value={avatarActionState}>
              <ul className="avatar-list">
                {visibleParticipants.map((user, index) => {
                  if (index > videoLayout.length - 1) {
                    return null;
                  }
                  const dimension = videoLayout[index];
                  const { width, height, x, y } = dimension;
                  const { height: canvasHeight } = canvasDimension;
                  return (
                    <Avatar
                      participant={user}
                      key={user.userId}
                      isActive={activeVideo === user.userId}
                      networkQuality={networkQuality[`${user.userId}`]}
                      style={{
                        width: `${width}px`,
                        height: `${height}px`,
                        top: `${canvasHeight - y - height}px`,
                        left: `${x}px`
                      }}
                    />
                  );
                })}
              </ul>
              <RemoteCameraControlPanel />
            </AvatarActionContext.Provider>
          </div>
        )} */}

        <div
          className={classnames('video-container', {
            'video-container-in-sharing-mobile': isRecieveSharing
          })}
          ref={videoContainerRef}
        >
          <canvas className="video-canvas" id="video-canvas" width="800" height="600" ref={videoRef} />
          {selfVideoLayout && mediaStream?.isRenderSelfViewWithVideoElement() && (
            <video
              id={SELF_VIDEO_ID}
              className="self-video-tag"
              playsInline
              muted
              autoPlay
              style={{
                display: 'block',
                width: `${selfVideoLayout.width}px`,
                height: `${selfVideoLayout.height}px`,
                top: `${selfVideoLayout.y}px`,
                left: `${selfVideoLayout.x}px`,
                pointerEvents: 'none'
              }}
            />
          )}
          <AvatarActionContext.Provider value={avatarActionState}>
            <ul className="avatar-list">
              {visibleParticipants.map((user, index) => {
                if (index > videoLayout.length - 1) {
                  return null;
                }
                const dimension = videoLayout[index];
                const { width, height, x, y } = dimension;
                const { height: canvasHeight } = canvasDimension;
                return (
                  <Avatar
                    participant={user}
                    key={user.userId}
                    isActive={activeVideo === user.userId}
                    networkQuality={networkQuality[`${user.userId}`]}
                    style={{
                      width: `${width}px`,
                      height: `${height}px`,
                      top: `${canvasHeight - y - height}px`,
                      left: `${x}px`
                    }}
                  />
                );
              })}
            </ul>
            <RemoteCameraControlPanel />
          </AvatarActionContext.Provider>
        </div>
        <VideoFooter
          togleChatView={handleChatButton}
          className="video-operations"
          sharing
          selfShareCanvas={shareViewRef.current?.selfShareRef}
          startPiPView={startPiPView}
        />

        {(totalPage > 1 && !isRecieveSharing) ||
          (totalPage > 1 && !isMobile() && isRecieveSharing && (
            <Pagination page={page} totalPage={totalPage} setPage={setPage} inSharing={isRecieveSharing} />
          ))}
        {/* 今のところ不要 */}
        {/* <ReportBtn /> */}
      </div>
      <ChatProvider>{isOpenChat && <ChatContainer onCloseChatView={handleChatButton} />}</ChatProvider>
    </Flex>
  );
};

export default VideoContainer;
